import { isTeamCoOwnerActor, isTeamOwnerActor } from './teamConfig';
import {
  CONDITION_OPERATOR_OPTIONS,
  findConflictingRetentionRule,
  findDifferentStartOverlap,
  findDuplicateRetentionRule,
  findOverlappingRetentionRule,
  formatConditionLine,
  getAvailableConditionFields,
  getConditionFieldDefinition,
  getConditionValueOptions,
  getFinalActionDefinition,
  getFinalActionLabel,
  getFinalActionOptions,
  getFinalActionScope,
  getRetentionPeriodCalculation,
  getRetentionPeriodDefinition,
  getRetentionPeriodLabel,
  getRetentionPeriodOptions,
  getRetentionRecordTypeLabel,
  getStartsWhenDefinition,
  getStartsWhenLabel,
  getStartsWhenMeaning,
  getStartsWhenOptions,
  isFinalActionAvailable,
  isRecordTypeAvailable,
  isRetentionPeriodAvailable,
  isStartsWhenAvailable,
  nextRetentionRuleId,
  normalizeRetentionForm,
  operatorIsMulti,
} from './retentionConfig';
import { emitRuleSaveHistory } from './changeHistoryEmitters';

const ACTOR = {
  name: 'Olivia Brown',
  role: 'Configuration Admin',
  team: 'Platform Operations',
  accountId: 'ADM-0002',
};

export function retentionActor() {
  return { ...ACTOR };
}

export function canAccessRetentionConfiguration(auth) {
  return isTeamOwnerActor(auth) || isTeamCoOwnerActor(auth);
}

export function canViewRetentionConfiguration(auth) {
  return canAccessRetentionConfiguration(auth);
}

export function canAddRetentionRule(auth) {
  return canAccessRetentionConfiguration(auth);
}

export function canEditRetentionRule(auth) {
  return canAccessRetentionConfiguration(auth);
}

export function canSaveRetentionRule(auth) {
  return canAccessRetentionConfiguration(auth);
}

function addError(result, key, message) {
  if (!result.summary.includes(message)) result.summary.push(message);
  if (key && !result.errors[key]) result.errors[key] = message;
}

function conditionValues(condition) {
  return [...new Set((condition?.values || []).map((value) => String(value || '').trim()).filter(Boolean))];
}

function conditionFingerprint(condition) {
  return [
    condition.field || '',
    condition.operator || '',
    conditionValues(condition).slice().sort().join(','),
  ].join('|');
}

function setsOverlap(a, b) {
  return a.some((value) => b.includes(value));
}

function conditionsCannotAllBeTrue(conditions = []) {
  const byField = {};
  conditions.forEach((condition) => {
    if (!condition.field || !condition.operator) return;
    const values = conditionValues(condition);
    if (!values.length) return;
    if (!byField[condition.field]) byField[condition.field] = [];
    byField[condition.field].push({ operator: condition.operator, values });
  });

  return Object.values(byField).some((entries) => {
    const equals = entries.filter((entry) => entry.operator === 'equals').flatMap((entry) => entry.values);
    const uniqueEquals = [...new Set(equals)];
    if (uniqueEquals.length > 1) return true;

    const anyOf = entries.filter((entry) => entry.operator === 'is_any_of').map((entry) => entry.values);
    if (anyOf.length > 1) {
      const intersection = anyOf.reduce((acc, set) => acc.filter((value) => set.includes(value)));
      if (!intersection.length) return true;
    }

    const positives = [
      ...uniqueEquals,
      ...(anyOf.length ? anyOf.reduce((acc, set) => acc.filter((value) => set.includes(value))) : []),
    ];
    const uniquePositives = [...new Set(positives)];

    return entries.some((entry) => {
      if (entry.operator === 'does_not_equal' && uniquePositives.includes(entry.values[0])) return true;
      if (entry.operator === 'is_not_any_of' && uniquePositives.length && uniquePositives.every((value) => entry.values.includes(value))) {
        return true;
      }
      if (entry.operator === 'is_any_of' && uniqueEquals.length && !setsOverlap(entry.values, uniqueEquals)) return true;
      return false;
    });
  });
}

export function collectRetentionSaveErrors(formData, allRules, excludeId, options = {}) {
  const form = normalizeRetentionForm(formData);
  const result = { errors: {}, summary: [] };
  const others = (allRules || []).filter((rule) => rule.id !== excludeId).map(normalizeRetentionForm);

  if (options.canSave === false) {
    addError(result, 'permission', 'You do not have permission to save this Rule.');
    return result;
  }

  if (options.checksAvailable === false) {
    addError(result, 'checksUnavailable', 'Required checks could not be completed. Try again.');
    return result;
  }

  if (!String(form.ruleName || '').trim()) addError(result, 'ruleName', 'Enter a Rule Name.');

  if (!form.recordType) addError(result, 'recordType', 'Select a Record Type.');
  else if (!isRecordTypeAvailable(form.recordType)) {
    addError(result, 'recordType', 'This selection is unavailable or incompatible.');
  }

  const startOptions = getStartsWhenOptions(form.recordType);
  if (!form.startsWhen) {
    addError(result, 'startsWhen', startOptions.length ? 'Select a Starts When.' : 'No compatible choices available');
  } else if (form.recordType && !startOptions.some((option) => option.value === form.startsWhen)) {
    addError(result, 'startsWhen', 'This selection is unavailable or incompatible.');
  } else if (!isStartsWhenAvailable(form.startsWhen) || !getStartsWhenDefinition(form.startsWhen)?.defined) {
    addError(result, 'startsWhen', 'The selected start is not fully defined.');
  } else if (!getStartsWhenMeaning(form.startsWhen) || !getStartsWhenDefinition(form.startsWhen)?.dateTimeBasis) {
    addError(result, 'startsWhen', 'The selected start is not fully defined.');
  }

  const periodOptions = getRetentionPeriodOptions(form.recordType);
  const period = getRetentionPeriodDefinition(form.retentionPeriod);
  if (!form.retentionPeriod) {
    addError(result, 'retentionPeriod', periodOptions.length ? 'Select a Retention Period.' : 'No compatible choices available');
  } else if (form.recordType && periodOptions.length && !periodOptions.some((option) => option.value === form.retentionPeriod)) {
    addError(result, 'retentionPeriod', 'This selection is unavailable or incompatible.');
  } else if (!isRetentionPeriodAvailable(form.retentionPeriod) || !period?.calculation) {
    addError(result, 'retentionPeriod', 'The selected period cannot be calculated.');
  }

  const actionOptions = getFinalActionOptions(form.recordType);
  const action = getFinalActionDefinition(form.finalAction);
  if (!form.finalAction) {
    addError(result, 'finalAction', actionOptions.length ? 'Select a Final Action.' : 'No compatible choices available');
  } else if (form.recordType && !actionOptions.some((option) => option.value === form.finalAction)) {
    addError(result, 'finalAction', 'This selection is unavailable or incompatible.');
  } else if (!isFinalActionAvailable(form.finalAction, form.recordType) || !action?.scope) {
    addError(result, 'finalAction', 'The selected action’s scope is not defined.');
  }

  const availableFields = getAvailableConditionFields(form.recordType, form.startsWhen).map((option) => option.value);
  const seenFingerprints = [];
  (form.conditions || []).forEach((condition, index) => {
    const key = `condition_${index}`;
    const values = conditionValues(condition);
    if (!condition.field || !condition.operator || !values.length) {
      addError(result, key, 'Complete or remove this Condition.');
      return;
    }
    const fieldDef = getConditionFieldDefinition(condition.field);
    if (!fieldDef || fieldDef.available === false) {
      addError(result, key, 'This selection is unavailable or incompatible.');
    } else if (fieldDef.repeatsScope) {
      addError(result, key, 'This Condition repeats information already selected.');
    } else if (!availableFields.includes(condition.field)) {
      addError(result, key, 'This selection is unavailable or incompatible.');
    }
    if (!CONDITION_OPERATOR_OPTIONS.some((option) => option.value === condition.operator)) {
      addError(result, key, 'This selection is unavailable or incompatible.');
    }
    if (!operatorIsMulti(condition.operator) && values.length !== 1) {
      addError(result, key, 'Complete or remove this Condition.');
    }
    const valueOptions = getConditionValueOptions(condition.field).map((option) => option.value);
    if (values.some((value) => valueOptions.length && !valueOptions.includes(value))) {
      addError(result, key, 'This selection is unavailable or incompatible.');
    }
    if (fieldDef && (fieldDef.checkableAtStart === false || fieldDef.checkableAtFinal === false)) {
      addError(result, key, 'This Condition cannot be checked at both required points.');
    }
    const fingerprint = conditionFingerprint(condition);
    if (seenFingerprints.includes(fingerprint)) addError(result, key, 'This Condition is already included.');
    seenFingerprints.push(fingerprint);
  });

  if (conditionsCannotAllBeTrue(form.conditions)) {
    addError(result, 'conditions', 'These Conditions cannot all be true together.');
  }

  const duplicate = findDuplicateRetentionRule(others, form);
  if (duplicate) addError(result, 'duplicate', `This Rule duplicates ${duplicate.id}.`);

  const conflict = findConflictingRetentionRule(others, form);
  if (conflict) addError(result, 'conflict', `This Rule conflicts with ${conflict.id}.`);

  const overlap = findOverlappingRetentionRule(others, form);
  if (overlap) addError(result, 'overlap', `This Rule overlaps ${overlap.id}.`);

  const differentStart = findDifferentStartOverlap(others, form);
  if (differentStart) {
    addError(result, 'differentStart', 'These Rules could affect the same record from different starts.');
  }

  if (excludeId && options.originalLastUpdated) {
    const current = (allRules || []).find((rule) => rule.id === excludeId);
    if (current && (current.lastUpdated || current.modifiedOn) && (current.lastUpdated || current.modifiedOn) !== options.originalLastUpdated) {
      addError(result, 'concurrent', 'This Rule has changed. Review the latest saved Rule.');
    }
  }

  return result;
}

export function ruleSnapshotFromForm(formData) {
  const form = normalizeRetentionForm(formData);
  return {
    ruleName: String(form.ruleName || '').trim(),
    recordType: form.recordType,
    startsWhen: form.startsWhen,
    retentionPeriod: form.retentionPeriod,
    finalAction: form.finalAction,
    conditions: (form.conditions || []).map((condition) => ({
      id: condition.id,
      field: condition.field,
      operator: condition.operator,
      values: conditionValues(condition),
    })),
  };
}

function snapshotsEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function getPublishedRule(rule) {
  if (!rule) return null;
  const { pendingDraft, ...published } = rule;
  return normalizeRetentionForm(published);
}

export function getEditRuleSource(rule) {
  if (!rule) return null;
  return normalizeRetentionForm(rule);
}

export function appendTestHistory(rule, summary, passed) {
  const actor = retentionActor();
  const base = Array.isArray(rule.testHistory) ? rule.testHistory : [];
  return [
    ...base,
    {
      actor: actor.name,
      role: actor.role,
      accountId: actor.accountId,
      timestamp: new Date().toISOString(),
      result: passed ? 'passed' : 'blocked',
      summary,
    },
  ];
}

export function mergeRulesAfterSave(allRules, payload, publishedBaseline) {
  const snapshot = ruleSnapshotFromForm(payload);
  const isNew = !publishedBaseline?.id;
  const previousSnapshot = publishedBaseline?.id ? ruleSnapshotFromForm(publishedBaseline) : null;

  if (!isNew && previousSnapshot && snapshotsEqual(previousSnapshot, snapshot)) {
    return {
      rules: allRules,
      record: getPublishedRule(publishedBaseline),
      unchanged: true,
      isNew: false,
    };
  }

  const nowIso = payload.lastUpdated || new Date().toISOString();
  const actor = payload.lastUpdatedBy || ACTOR.name;
  const id = payload.id || publishedBaseline?.id || nextRetentionRuleId(allRules);
  const record = {
    ...snapshot,
    id,
    dateAdded: isNew ? nowIso : publishedBaseline.dateAdded || publishedBaseline.createdOn || nowIso,
    addedBy: isNew ? actor : publishedBaseline.addedBy || publishedBaseline.createdBy || actor,
    lastUpdated: nowIso,
    lastUpdatedBy: actor,
    testHistory: payload.testHistory || publishedBaseline?.testHistory || [],
    configurationHistory: payload.configurationHistory || publishedBaseline?.configurationHistory || [],
  };
  try {
    emitRuleSaveHistory({
      isNew,
      previous: publishedBaseline,
      next: record,
      module: 'Configuration — Retention',
      sourceModuleRef: 'configuration_retention',
      changedItem: record.ruleName || record.name || id,
      itemId: id,
      changeReason: payload.changeReason,
      actor,
      stamp: nowIso,
      compareKeys: [
        'ruleName',
        'recordType',
        'startsWhen',
        'retentionPeriod',
        'finalAction',
        'conditions',
      ],
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }
  if (isNew) {
    return {
      rules: [...allRules.filter((rule) => rule.id !== record.id), record],
      record,
      unchanged: false,
      isNew: true,
    };
  }
  return {
    rules: allRules.map((rule) => (rule.id === record.id ? { ...rule, ...record } : rule)),
    record,
    unchanged: false,
    isNew: false,
  };
}

export function saveRetentionRule(allRules, payload, publishedBaseline, options = {}) {
  const excludeId = publishedBaseline?.id || payload?.id || undefined;
  const validation = collectRetentionSaveErrors(payload, allRules, excludeId, {
    originalLastUpdated:
      options.originalLastUpdated
      ?? publishedBaseline?.lastUpdated
      ?? publishedBaseline?.modifiedOn,
    canSave: options.canSave !== false,
    checksAvailable: options.checksAvailable !== false,
  });

  if (validation.summary.length) {
    return {
      success: false,
      errors: validation.errors,
      summary: validation.summary,
      rules: allRules,
      unchanged: false,
    };
  }

  try {
    const merged = mergeRulesAfterSave(allRules, payload, publishedBaseline || {});
    return {
      success: true,
      errors: {},
      summary: [],
      rules: merged.rules,
      record: merged.record,
      unchanged: Boolean(merged.unchanged),
      isNew: Boolean(merged.isNew),
    };
  } catch {
    return {
      success: false,
      errors: { form: 'The Rule could not be saved. Your changes have been kept for review.' },
      summary: ['The Rule could not be saved. Your changes have been kept for review.'],
      rules: allRules,
      unchanged: false,
    };
  }
}

export function mergeRulesAfterActivate(allRules, payload, publishedBaseline) {
  const result = saveRetentionRule(allRules, payload, publishedBaseline || {}, {
    canSave: true,
    checksAvailable: true,
  });
  return result.rules;
}

export function collectActivationErrors(formData, allRules, excludeId) {
  return collectRetentionSaveErrors(formData, allRules, excludeId).summary;
}

export function runRetentionRuleTest(formData, allRules) {
  const snapshot = ruleSnapshotFromForm(formData);
  const validation = collectRetentionSaveErrors(formData, allRules, formData.id || undefined);
  const lines = [
    `Record Type: ${getRetentionRecordTypeLabel(snapshot.recordType) || '—'}`,
    `Starts When: ${getStartsWhenLabel(snapshot.startsWhen) || '—'}`,
    `Start meaning: ${getStartsWhenMeaning(snapshot.startsWhen) || '—'}`,
    `Retention Period: ${getRetentionPeriodLabel(snapshot.retentionPeriod) || '—'}`,
    `Period calculation: ${getRetentionPeriodCalculation(snapshot.retentionPeriod) || '—'}`,
    `Final Action: ${getFinalActionLabel(snapshot.finalAction, snapshot.recordType) || '—'}`,
    `Final Action scope: ${getFinalActionScope(snapshot.finalAction) || '—'}`,
  ];
  if ((snapshot.conditions || []).length) {
    lines.push('', 'Conditions:', ...snapshot.conditions.map((condition) => `• ${formatConditionLine(condition)}`));
  } else {
    lines.push('', 'No Conditions');
  }
  if (validation.summary.length) {
    return {
      passed: false,
      summary: 'Rule test blocked. Configuration conflicts or missing requirements were found.',
      detail: [...lines, '', 'Issues:', ...validation.summary.map((message) => `• ${message}`)].join('\n'),
    };
  }
  return {
    passed: true,
    summary: 'Rule test passed. Configuration is valid — no records were modified.',
    detail: [...lines, '', 'No duplicate, conflicting or overlapping Rules detected.'].join('\n'),
  };
}

export function mergeRulesAfterSaveDraft(allRules, payload, publishedBaseline) {
  return mergeRulesAfterSave(allRules, payload, publishedBaseline).rules;
}

export function mergeRulesAfterInactivate(allRules) {
  return allRules;
}

export function mergeRulesAfterListActivate(allRules) {
  return allRules;
}

export { normalizeRetentionForm };
